<template>
  <div
    v-if="dialog"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    @click.self="close"
  >
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-fadeIn"
    >
      <div class="p-6">
        <div class="flex justify-between items-center mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-xl font-medium text-gray-800 dark:text-gray-200 flex items-center">
            <UsersIcon class="w-6 h-6 mr-2" />
            Junção de Pacientes
          </h3>
          <button
            class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            @click="close"
          >
            <XIcon class="w-6 h-6" />
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Seção Paciente Origem -->
          <div class="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <h4 class="font-semibold mb-2 text-gray-700 dark:text-gray-300">Paciente Origem (será desativado)</h4>
            <div class="relative">
              <select
                v-model="sourcePatientId"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
              >
                <option :value="null" disabled>Selecione um paciente</option>
                <option v-for="p in availableSourcePatients" :key="p.id" :value="p.id">
                  {{ p.name }} - {{ p.cpf || p.cns || p.documentValue }}
                </option>
              </select>
            </div>
            <div v-if="sourcePatient" class="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
              <h5 class="font-bold">{{ sourcePatient.name }}</h5>
              <p class="text-sm text-gray-600 dark:text-gray-400">ID: {{ sourcePatient.id }}</p>
              <p class="text-sm text-gray-600 dark:text-gray-400">CPF: {{ sourcePatient.cpf || 'Não informado' }}</p>
              <p class="text-sm text-gray-600 dark:text-gray-400">Nascimento: {{ formatDate(sourcePatient.birthdate) }}</p>
            </div>
          </div>

          <!-- Seção Paciente Destino -->
          <div class="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <h4 class="font-semibold mb-2 text-gray-700 dark:text-gray-300">Paciente Destino (manterá os dados)</h4>
            <div class="relative">
              <select
                v-model="destinationPatientId"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
              >
                <option :value="null" disabled>Selecione um paciente</option>
                <option v-for="p in availableDestinationPatients" :key="p.id" :value="p.id">
                  {{ p.name }} - {{ p.cpf || p.cns || p.documentValue }}
                </option>
              </select>
            </div>
            <div v-if="destinationPatient" class="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
              <h5 class="font-bold">{{ destinationPatient.name }}</h5>
              <p class="text-sm text-gray-600 dark:text-gray-400">ID: {{ destinationPatient.id }}</p>
              <p class="text-sm text-gray-600 dark:text-gray-400">CPF: {{ destinationPatient.cpf || 'Não informado' }}</p>
              <p class="text-sm text-gray-600 dark:text-gray-400">Nascimento: {{ formatDate(destinationPatient.birthdate) }}</p>
            </div>
          </div>
        </div>

        <!-- Seção de Confirmação -->
        <div class="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div class="flex items-start">
            <AlertTriangleIcon class="w-6 h-6 text-yellow-500 dark:text-yellow-400 mr-3 mt-1" />
            <div>
              <h4 class="font-semibold text-yellow-800 dark:text-yellow-200">Ação Irreversível</h4>
              <p class="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Todos os agendamentos, exames e outros dados do paciente de origem serão transferidos para o paciente de destino. O paciente de origem será desativado. Esta ação não pode ser desfeita.
              </p>
              <div class="mt-4 flex items-center">
                <input
                  type="checkbox"
                  id="confirmMerge"
                  v-model="confirmMerge"
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label for="confirmMerge" class="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                  Eu entendo e quero continuar.
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Ações do Modal -->
        <div class="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
            @click="close"
          >
            Cancelar
          </button>
          <button
            class="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors shadow-sm disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
            :disabled="!isMergeReady"
            @click="merge"
          >
            Juntar Pacientes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { UsersIcon, XIcon, AlertTriangleIcon } from 'lucide-vue-next';

const props = defineProps({
  dialog: {
    type: Boolean,
    required: true,
  },
  patients: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['close', 'merge']);

const sourcePatientId = ref(null);
const destinationPatientId = ref(null);
const confirmMerge = ref(false);

const sourcePatient = computed(() => 
  props.patients.find(p => p.id === sourcePatientId.value)
);

const destinationPatient = computed(() =>
  props.patients.find(p => p.id === destinationPatientId.value)
);

const availableSourcePatients = computed(() => {
  return props.patients.filter(p => p.id !== destinationPatientId.value);
});

const availableDestinationPatients = computed(() => {
  return props.patients.filter(p => p.id !== sourcePatientId.value);
});

const isMergeReady = computed(() => 
  sourcePatientId.value &&
  destinationPatientId.value &&
  sourcePatientId.value !== destinationPatientId.value &&
  confirmMerge.value
);

function close() {
  resetState();
  emit('close');
}

function merge() {
  if (isMergeReady.value) {
    emit('merge', {
      sourceId: sourcePatientId.value,
      destinationId: destinationPatientId.value,
    });
    resetState();
  }
}

function resetState() {
  sourcePatientId.value = null;
  destinationPatientId.value = null;
  confirmMerge.value = false;
}

function formatDate(dateString) {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  if (!year || !month || !day) return dateString;
  return `${day}/${month}/${year}`;
}

watch(() => props.dialog, (newVal) => {
  if (!newVal) {
    resetState();
  }
});
</script>